// Wrapper d'un widget instancié dans la grille. Gère :
// - lookup du composant via WIDGET_COMPONENTS
// - fallback si widget inconnu (id du catalogue serveur non mappé)
// - mode édition : poignée + bouton on/off + flèches ordre + select taille
import { ChevronDown, ChevronUp, EyeOff, GripVertical } from "lucide-react";
import { WIDGET_COMPONENTS } from "../../widgets/registry";

function WidgetUnknown({ widgetId }) {
  return (
    <div
      className="rounded-xl p-5 h-full flex flex-col items-center justify-center gap-2"
      style={{
        background: "var(--oh-bg-surface)",
        border: "1px dashed var(--oh-border)",
        color: "var(--oh-text-muted)",
      }}
    >
      <p className="text-xs uppercase tracking-wider">Widget inconnu</p>
      <code className="text-xs" style={{ color: "var(--oh-text)" }}>{widgetId}</code>
      <p className="text-xs text-center">
        Le serveur référence ce widget mais aucun composant frontend ne lui est associé.
      </p>
    </div>
  );
}

export default function WidgetCard({
  widget,
  meta,
  editMode = false,
  canMoveUp = true,
  canMoveDown = true,
  onToggle,
  onMove,
  onResize,
}) {
  const Component = WIDGET_COMPONENTS[widget.id] || null;
  const title = meta?.title;
  const description = meta?.description;

  return (
    <div className="relative h-full">
      {Component ? (
        <Component
          widgetId={widget.id}
          title={title}
          description={description}
          config={widget.config || {}}
        />
      ) : (
        <WidgetUnknown widgetId={widget.id} />
      )}

      {editMode && (
        <div
          className="absolute top-2 right-2 flex items-center gap-1 rounded-md px-1 py-0.5"
          style={{
            background: "var(--oh-bg-elevated)",
            border: "1px solid var(--oh-border)",
            boxShadow: "var(--oh-shadow-sm, 0 1px 2px rgba(0,0,0,0.1))",
          }}
          data-testid={`widget-edit-${widget.id}`}
        >
          <button
            type="button"
            onClick={() => onMove?.(widget.id, "up")}
            disabled={!canMoveUp}
            aria-label="Monter le widget"
            className="p-1 rounded disabled:opacity-30"
            style={{ color: "var(--oh-text)" }}
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove?.(widget.id, "down")}
            disabled={!canMoveDown}
            aria-label="Descendre le widget"
            className="p-1 rounded disabled:opacity-30"
            style={{ color: "var(--oh-text)" }}
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <select
            value={widget.size || "md"}
            onChange={(e) => onResize?.(widget.id, e.target.value)}
            aria-label="Taille du widget"
            className="text-xs px-1 py-0.5 rounded"
            style={{
              background: "var(--oh-bg-surface)",
              color: "var(--oh-text)",
              border: "1px solid var(--oh-border)",
            }}
          >
            <option value="sm">S</option>
            <option value="md">M</option>
            <option value="lg">L</option>
            <option value="xl">XL</option>
          </select>
          <button
            type="button"
            onClick={() => onToggle?.(widget.id)}
            aria-label="Masquer le widget"
            className="p-1 rounded"
            style={{ color: "var(--oh-error-text, #f87171)" }}
          >
            <EyeOff className="w-3.5 h-3.5" />
          </button>
          <span
            className="px-1"
            style={{ color: "var(--oh-text-muted)", cursor: "grab" }}
            title="Réservé au D&D futur (L3-bis)"
          >
            <GripVertical className="w-3.5 h-3.5" />
          </span>
        </div>
      )}
    </div>
  );
}
